import { CheckCell, HelpTipHeader, MultiTextCell, MultiTextCellData, Table, TableHeader, TextCell } from '@amzn/aapn-react-components'
import { toNullable } from 'fp-ts/es6/Option'
import React, { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react'
import { AdvertiserAccessLevel, PartnerAdvertiserEdge, Right, useGetAdvertisersQuery } from 'generated/graphql'
import { Trans, useTranslation } from 'react-i18next'
import {
  ADVERTISERS_ERROR_CALL_TO_ACTION,
  ADVERTISERS_ERROR_HEADER,
  ADVERTISERS_ERROR_MESSAGE,
  NO_ADVERTISERS_DATA_BODY,
  NO_ADVERTISERS_DATA_HEADER,
  NO_ADVERTISERS_RESULTS_BODY,
  NO_ADVERTISERS_RESULTS_HEADER,
  TABLE_HEADER_ACCOUNT,
  TABLE_HEADER_TYPE,
  TABLE_HEADER_ACCESS_LEVEL,
  TABLE_HEADER_INFO_TIP_ACCESS_LEVEL,
} from './manage_advertisers_v2_i18n_mappings'
import { PARTNER_ID_HEADER_KEY } from 'util/app_initialization/apollo_client/stagedive_apollo_client_provider'
import { getAdvertiserEntityTypeTranslation } from 'util/advertiser_type_translation'
import Skeleton from 'react-loading-skeleton'
import { SKELETON_NUM_ROW } from '../../../manage_shared_constants'
import { UseRowSelectInstanceProps, UseTableInstanceProps } from 'react-table'
import { useLoginContext } from 'stagedive/LoginContext'
import { AdvertisersNoDataBody } from './advertisers_no_data_body'
import { AdvertisersNoResultsBody } from './advertisers_no_results_body'
import { SingleTranslationMultiTextCell, SingleTranslationMultiTextCellData } from './single_translation_multi_text_cell'
import { HeaderContainer, NewlineInfoTip, StyledAdvertiserReportExport, AdvertisersBody, StyledTableContainer } from './manage_advertisers_v2.styles'
import { ManageAdvertisersCheckAllHeader } from './manage_advertisers_check_all_header'
import { AdvertisersPagination } from './advertisers_pagination'
import { addNewCursor, advertiserAccessLevelLabel, PrevCursor } from './advertisers_helper'
import { TableLoadErrorBody } from '../../../../../shared/components/errors/error_table_load'
import { BulkEditHeaderV2 } from './bulk_unlink_v2/bulk_unlink_header_v2'
import { UnlinkAdvertisersResult } from './bulk_manage'

// TODO: Implement full pagination when it's available from Arches: https://sim.amazon.com/issues/AAPN-1782
export const PAGE_SIZE = 50
export const COUNTRY_CODE_PREFIX = 'COUNTRY_CODE:'

interface ManageAdvertisersV2 {
  setUnlinkRequestResult: Dispatch<SetStateAction<UnlinkAdvertisersResult>>
  unlinkRequestResult: UnlinkAdvertisersResult
}
export const ManageAdvertisersV2: React.FunctionComponent<ManageAdvertisersV2> = (props: ManageAdvertisersV2): JSX.Element => {
  const { unlinkRequestResult, setUnlinkRequestResult } = props
  const [advertisers, setAdvertisers] = useState<PartnerAdvertiserEdge[]>([])
  const [partnerCompanyName, setPartnerCompanyName] = useState('')
  const [pageSize, setPageSize] = useState<number>(10)
  const { loginContext } = useLoginContext()
  const partnerAccountId = toNullable(loginContext.partnerProfileId) ?? ''
  const userRights = loginContext.userRights
  const { t } = useTranslation()
  const hasSetData = useRef(false)
  const [pageNum, setPageNum] = useState<number>(0)
  const [cursor, setCursor] = useState<string | null>(null)
  const [prevCursors, setPrevCursors] = useState<PrevCursor[]>([])

  const {
    loading,
    refetch,
    error,
    data: response,
  } = useGetAdvertisersQuery({
    context: {
      headers: {
        [PARTNER_ID_HEADER_KEY]: partnerAccountId,
      },
    },
    variables: {
      after: cursor,
    },
  })

  useEffect(() => {
    if (!loading) {
      if (response?.partner?.advertisers?.edges?.length) {
        addNewCursor(prevCursors, pageNum, setPrevCursors, response?.partner?.advertisers?.pageInfo?.endCursor)
      } else {
        setPageNum(0)
        setCursor(null)
      }
    }
  }, [response, pageNum, prevCursors, loading])

  const sortedAdvertisers = (list: PartnerAdvertiserEdge[]): PartnerAdvertiserEdge[] => {
    return Array.from(list).sort((firstAdvertiser: PartnerAdvertiserEdge, secondAdvertiser: PartnerAdvertiserEdge) => {
      const firstAdvertiserName = firstAdvertiser?.node.name ?? ''
      const secondAdvertiserName = secondAdvertiser?.node?.name ?? ''
      if (firstAdvertiserName > secondAdvertiserName) {
        return 1
      }
      if (firstAdvertiserName === secondAdvertiserName) {
        return 0
      }
      return -1
    })
  }

  useEffect(() => {
    setPartnerCompanyName(response?.partner?.liveRegistration?.registration?.companyName as string)
    const nullableAdvertisers = response?.partner?.advertisers?.edges as PartnerAdvertiserEdge[]
    if (nullableAdvertisers) {
      setAdvertisers(
        sortedAdvertisers(nullableAdvertisers).map((advertiser) => {
          return { ...advertiser, disabled: advertiser.accessLevel === AdvertiserAccessLevel.EntityNoConsoleAccess }
        }),
      )
      hasSetData.current = true
    }
  }, [response])
  const columns = React.useMemo(() => {
    const fullAccessColumns = [
      {
        id: 'bulk',
        // eslint-disable-next-line react/display-name
        Header: (props: React.PropsWithChildren<UseRowSelectInstanceProps<PartnerAdvertiserEdge>>) => (
          <BulkEditHeaderV2
            {...props}
            setAdvertisers={setAdvertisers}
            setPageNum={setPageNum}
            setCursor={setCursor}
            partnerCompanyName={partnerCompanyName}
            refetchAdvertisers={refetch}
            setUnlinkRequestResult={setUnlinkRequestResult}
            unlinkRequestResult={unlinkRequestResult}
          />
        ),
        columns: [
          {
            id: 'selection',
            Header: ManageAdvertisersCheckAllHeader,
            Cell: hasSetData.current ? CheckCell : Skeleton,
            flexGrow: 0,
            width: 35,
          },
          {
            id: t(TABLE_HEADER_ACCOUNT),
            Header: TableHeader,
            Cell: hasSetData.current ? MultiTextCell : Skeleton,
            accessor: (originalRow: object): MultiTextCellData => {
              const advertiser = originalRow as PartnerAdvertiserEdge
              if (advertiser.constructor === Object && Object.keys(advertiser).length === 0) {
                return {
                  focusPrimary: true,
                  lines: ['', ''],
                }
              }
              const primaryText = advertiser?.node?.name ?? ''
              const secondaryText = advertiser?.node?.id ?? ''
              const countryCode = advertiser?.node?.countryCode ?? ''
              return {
                focusPrimary: true,
                lines: [primaryText, `${secondaryText}, ${t(`${COUNTRY_CODE_PREFIX}${countryCode}`)}`],
              }
            },
          },
          {
            id: t(TABLE_HEADER_TYPE),
            Header: TableHeader,
            Cell: hasSetData.current ? TextCell : Skeleton,
            flexGrow: 0.5,
            accessor: (originalRow: object): string => {
              const advertiser = originalRow as PartnerAdvertiserEdge
              if (advertiser?.node?.type) return getAdvertiserEntityTypeTranslation(advertiser?.node?.type as string, t)
              return ''
            },
          },
          {
            id: t(TABLE_HEADER_ACCESS_LEVEL),
            // eslint-disable-next-line react/display-name
            Header: (props: UseTableInstanceProps<object>) => (
              <HelpTipHeader
                {...props}
                columnLabel={t(TABLE_HEADER_ACCESS_LEVEL)}
                helpTipLabel={
                  <NewlineInfoTip>
                    <Trans i18nKey={TABLE_HEADER_INFO_TIP_ACCESS_LEVEL} components={[<strong key={'key'} />]} />
                  </NewlineInfoTip>
                }
              />
            ),
            Cell: hasSetData.current ? SingleTranslationMultiTextCell : Skeleton,
            flexGrow: 0.5,
            accessor: (originalRow: object): SingleTranslationMultiTextCellData => {
              const advertiser = originalRow as PartnerAdvertiserEdge
              const advertiserAccessLevel = advertiser.accessLevel ?? AdvertiserAccessLevel.EntityNoConsoleAccess
              return { translationKey: advertiserAccessLevelLabel.get(advertiserAccessLevel) ?? 'ADVERTISERS:TABLE:ACCESS_LEVEL:NONE' }
            },
          },
        ],
      },
    ]

    const includeActions: boolean = userRights.has(Right.AdvertiserMappingEdit)

    const filteredColumns = fullAccessColumns[0].columns.filter((column) => {
      if (!includeActions) {
        return column.id !== 'selection'
      } else {
        return true
      }
    })

    if (includeActions) {
      // Keep the bulk action header
      return [
        {
          ...fullAccessColumns[0],
          columns: filteredColumns,
        },
      ]
    } else {
      return filteredColumns
    }
  }, [t, userRights, partnerAccountId, partnerCompanyName, refetch])

  const tableProps = {
    columns: columns,
    data: !hasSetData.current ? Array(SKELETON_NUM_ROW).fill({}) : advertisers,
    hasRefinementsSet: false,
    hasLoadError: !!error || (!response && !loading),
    // eslint-disable-next-line react/display-name
    NoDataBody: () => <AdvertisersNoDataBody noDataBodyLabel={t(NO_ADVERTISERS_DATA_BODY)} noDataHeaderLabel={t(NO_ADVERTISERS_DATA_HEADER)} />,
    // eslint-disable-next-line react/display-name
    NoResultsBody: () => (
      <AdvertisersNoResultsBody noResultsBodyLabel={t(NO_ADVERTISERS_RESULTS_BODY)} noResultsHeaderLabel={t(NO_ADVERTISERS_RESULTS_HEADER)} />
    ),
    // eslint-disable-next-line react/display-name
    ErrorBody: () => (
      <TableLoadErrorBody
        errorHeaderLabel={t(ADVERTISERS_ERROR_HEADER)}
        errorBodyLabel={t(ADVERTISERS_ERROR_MESSAGE)}
        errorCallToAction={t(ADVERTISERS_ERROR_CALL_TO_ACTION)}
      />
    ),
  }

  const advertisersPaginationProps = {
    loading,
    cursor: response?.partner?.advertisers?.pageInfo?.endCursor,
    setCursor,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    prevCursors,
    hasNextPage: response?.partner?.advertisers?.pageInfo?.hasNextPage,
  }
  return (
    <AdvertisersBody>
      <HeaderContainer>
        <StyledAdvertiserReportExport />
      </HeaderContainer>
      <StyledTableContainer>
        <Table {...tableProps} />
        <AdvertisersPagination {...advertisersPaginationProps} />
      </StyledTableContainer>
    </AdvertisersBody>
  )
}
